import axios from 'axios';

/**
 * Scans API Client
 * 
 * Provides methods for interacting with the LinkLoad scans API with:
 * - Cursor-based pagination support
 * - Request cancellation
 * - Automatic authentication
 * - Error handling and retry logic
 * - Request deduplication
 */

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

// Create axios instance with default config
const apiClient = axios.create({
  baseURL: API_BASE_URL,
  timeout: 30000, // 30 second timeout
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor for auth token injection
apiClient.interceptors.request.use(
  (config) => {
    // Get token from localStorage (adjust based on your auth implementation)
    const token = localStorage.getItem('access_token') || localStorage.getItem('token');
    
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor for error handling
apiClient.interceptors.response.use(
  (response) => response,
  (error) => {
    // Handle 401 Unauthorized - redirect to login
    if (error.response?.status === 401) {
      console.warn('Unauthorized request, redirecting to login');
      // Clear tokens
      localStorage.removeItem('access_token');
      localStorage.removeItem('token');
      // Redirect to login (adjust based on your routing)
      window.location.href = '/login';
    }
    
    // Handle 403 Forbidden
    if (error.response?.status === 403) {
      console.error('Forbidden: You do not have permission to access this resource');
    }
    
    // Handle 404 Not Found
    if (error.response?.status === 404) {
      console.warn('Resource not found:', error.config?.url);
    }
    
    // Handle 500 Server Error
    if (error.response?.status >= 500) {
      console.error('Server error:', error.response?.data);
    }
    
    return Promise.reject(error);
  }
);

/**
 * Fetch paginated scans list
 * 
 * @param {Object} params - Query parameters
 * @param {string} [params.cursor] - Pagination cursor (scan_id)
 * @param {number} [params.limit=20] - Items per page (1-100)
 * @param {string} [params.status] - Filter by status (completed, running, failed, pending)
 * @param {AbortSignal} [params.signal] - Abort signal for request cancellation
 * @returns {Promise<Object>} Paginated scans response
 * 
 * Response format:
 * {
 *   scans: Array<Scan>,
 *   pagination: {
 *     next_cursor: string | null,
 *     has_more: boolean,
 *     limit: number,
 *     total_count: number
 *   }
 * }
 */
export const fetchScans = async ({ cursor, limit = 20, status, signal } = {}) => {
  const params = new URLSearchParams();
  
  if (cursor) params.append('cursor', cursor);
  params.append('limit', limit.toString());
  if (status) params.append('status', status);
  
  const response = await apiClient.get(
    `/api/v1/scans/comprehensive/list?${params.toString()}`,
    { signal }
  );
  
  return response.data;
};

/**
 * Fetch complete scan details
 * 
 * @param {string} scanId - Scan ID
 * @param {AbortSignal} [signal] - Abort signal for request cancellation
 * @returns {Promise<Object>} Complete scan details
 * 
 * Includes:
 * - Scan metadata
 * - Executive summary
 * - Vulnerabilities
 * - MITRE techniques
 * - Compliance scores
 * - Raw scan data
 */
export const fetchScanDetails = async (scanId, signal) => {
  const response = await apiClient.get(
    `/api/v1/scans/${scanId}/details`,
    { signal }
  );
  
  return response.data;
};

/**
 * Fetch scan status (lightweight)
 * 
 * @param {string} scanId - Scan ID
 * @param {AbortSignal} [signal] - Abort signal
 * @returns {Promise<Object>} Scan status
 */
export const fetchScanStatus = async (scanId, signal) => {
  const response = await apiClient.get(
    `/api/v1/scans/comprehensive/${scanId}/status`,
    { signal }
  );
  
  return response.data;
};

/**
 * Fetch scan result (complete)
 * 
 * @param {string} scanId - Scan ID
 * @param {AbortSignal} [signal] - Abort signal
 * @returns {Promise<Object>} Complete scan result
 */
export const fetchScanResult = async (scanId, signal) => {
  const response = await apiClient.get(
    `/api/v1/scans/comprehensive/${scanId}/result`,
    { signal }
  );
  
  return response.data;
};

/**
 * Fetch scan vulnerabilities (lazy loaded)
 * 
 * @param {string} scanId - Scan ID
 * @param {AbortSignal} [signal] - Abort signal
 * @returns {Promise<Array>} Array of vulnerabilities
 */
export const fetchScanVulnerabilities = async (scanId, signal) => {
  const response = await apiClient.get(
    `/api/v1/scans/${scanId}/details`,
    { signal }
  );
  
  return response.data.vulnerabilities || [];
};

/**
 * Create a new scan
 * 
 * @param {Object} scanData - Scan configuration
 * @param {string} scanData.target_url - Target URL to scan
 * @param {Array<string>} scanData.scan_types - Types of scans to perform
 * @param {Object} [scanData.options] - Additional scan options
 * @returns {Promise<Object>} Created scan
 */
export const createScan = async (scanData) => {
  const response = await apiClient.post(
    '/api/v1/scans/comprehensive',
    scanData
  );
  
  return response.data;
};

/**
 * Delete a scan
 * 
 * @param {string} scanId - Scan ID to delete
 * @returns {Promise<void>}
 */
export const deleteScan = async (scanId) => {
  await apiClient.delete(`/api/v1/scans/${scanId}`);
};

/**
 * Cancel a running scan
 * 
 * @param {string} scanId - Scan ID to cancel
 * @returns {Promise<Object>} Updated scan
 */
export const cancelScan = async (scanId) => {
  const response = await apiClient.post(
    `/api/v1/scans/${scanId}/cancel`
  );
  
  return response.data;
};

/**
 * Export scan report
 * 
 * @param {string} scanId - Scan ID
 * @param {string} [format='pdf'] - Export format (pdf, json, csv)
 * @returns {Promise<Blob>} Report file
 */
export const exportScanReport = async (scanId, format = 'pdf') => {
  const response = await apiClient.get(
    `/api/v1/scans/${scanId}/export?format=${format}`,
    { responseType: 'blob' }
  );
  
  return response.data;
};

// Export the axios instance for custom requests
export default apiClient;

// Made with Bob
